import React, { PropsWithChildren, useId, useState } from 'react'
import { EditShiftWorkFlowSchema } from '../schemas/editShiftWorkFlowSchema'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { ShiftBonus } from 'api/typings'
import { useEditShiftWorkMutation } from 'queries/shifts'
import {
  EditWorkFlowState,
  useGlobalWorkflowsContextValue,
} from '../../GlobalWorkflowManager/GlobalWorkflowManager'

type EditShiftWorkFlowProviderContextState = {
  actions: {
    submitFormToServer: ReturnType<typeof useEditShiftWorkMutation>['mutate']
    setFormData: (data: Partial<EditShiftWorkFlowSchema>) => void
    close: () => void
  }
  state: {
    isOpen: boolean
    worker: {
      id: number
      name: string
    }
    workId: number
    shiftId: number
    shiftStartsAt: string
    shiftEndsAt: string
    payRate?: number
    payLumpSum?: number
    shiftBonuses?: ShiftBonus[]
    lunchBreak?: { type: 'paid' | 'unpaid'; durationInMinutes: number }
    timezone: string
    formData: Partial<EditShiftWorkFlowSchema>
    mutationState: {
      isPending: boolean
      isError: boolean
      errorMessage: string | undefined
    }
  }
}

const [Context, useEditShiftWorkFlowContextValue] =
  createContext<EditShiftWorkFlowProviderContextState>({
    name: 'EditShiftWorkFlowContext',
  })

type EditShiftWorkFlowContextProviderProps = PropsWithChildren<{
  initialData: EditWorkFlowState
  isOpen: boolean
  close: () => void
}>

const EditShiftWorkFlowContextProvider = ({
  children,
  initialData: {
    shiftId,
    workId,
    status,
    clockedInAt,
    clockedOutAt,
    leftEarly,
    timezone,
    worker,
    shiftStartsAt,
    shiftEndsAt,
    payRate,
    payLumpSum,
    shiftBonuses,
    lunchBreak,
  },
  isOpen,
  close,
}: EditShiftWorkFlowContextProviderProps) => {
  const [formData, setFormData] = useState<Partial<EditShiftWorkFlowSchema>>({
    status,
    clockedInAtAndClockedOutAt: [clockedInAt, clockedOutAt],
    leftEarly,
    existingValues: {
      status,
      clockedInAtAndClockedOutAt: [clockedInAt, clockedOutAt],
      leftEarly,
    },
    bypassMaxDaysForEditWork: false,
  })

  const { mutate, isPending, isError, error } =
    useEditShiftWorkMutation(shiftId)

  const value: EditShiftWorkFlowProviderContextState = {
    actions: {
      submitFormToServer: mutate,
      setFormData,
      close,
    },
    state: {
      isOpen,
      worker,
      workId,
      shiftId,
      shiftStartsAt,
      shiftEndsAt,
      payRate,
      payLumpSum,
      shiftBonuses,
      lunchBreak,
      timezone,
      formData,
      mutationState: {
        isPending,
        isError,
        errorMessage: parseErrorMessage(error),
      },
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const EditShiftWorkFlowProvider = ({ children }: PropsWithChildren) => {
  const {
    editShiftWorkFlow: { isOpen, data, close },
  } = useGlobalWorkflowsContextValue()

  const baseId = useId()

  if (!isOpen || !data) {
    return null
  }

  return (
    <EditShiftWorkFlowContextProvider
      initialData={data}
      // resets the initial state when the modal is opened or closed
      key={`${baseId}-${isOpen}`}
      isOpen={isOpen}
      close={close}
    >
      {children}
    </EditShiftWorkFlowContextProvider>
  )
}

export { useEditShiftWorkFlowContextValue }
